import { FC, useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { clsx } from 'clsx'

import { Box, Button, Typography, useMediaQuery } from '@mui/material'
import { ClassNameMap, withStyles } from '@mui/styles'

import { SectionHeader } from 'components/common'
import { DropDown, DropDownContent, DropDownOptions } from 'components/core/DropDown/DropDown-component'
import { usePermissions } from 'hooks/usePermissions'
import { useStyles } from 'pages/MockAccount/styles'
import { useAppSelector } from 'redux/hooks'
import { IMAGES_PATH, theme, themeStyles } from 'themes'
import combineStyles from 'utils/combineStyles'
import { generateMockAssets } from 'utils/mockAssetGeneratorHelper'

import { COMPONENT_EXTENDER } from 'extender'

const HomePageLayout = COMPONENT_EXTENDER('components/layouts/HomePageLayout')
const Sections = COMPONENT_EXTENDER('components/core/Sections/Sections-component')

const tooltipInfo = IMAGES_PATH('/tooltip-info.svg')

const successImg = IMAGES_PATH('/successBig.svg')

const [MockAssets, MockDashboard] = generateMockAssets()

interface IProps {
    classes: ClassNameMap
}

const AccountMockPage: FC<IProps> = ({ classes }) => {
    const {
        currencies,
        user: { currentUser },
    } = useAppSelector(state => state)
    const style = useStyles()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const mobile = useMediaQuery(theme.breakpoints.down('md'))
    const { getPermission } = usePermissions()

    const isCompany = !!currentUser?.isCompany
    const accountVerificationPending = currentUser?.kycStatus === 'SentForKyc' || currentUser?.kycStatus === 'Verified'
    const previewModeAllowed = getPermission('PreviewModeEnabled')

    useEffect(() => {
        // If user is not onboarding or preview mode flag set (by going through url directly), sending back to wizard
        if (!!currentUser && (!previewModeAllowed || localStorage.getItem('isUserInPreviewMode') !== 'true')) {
            localStorage.removeItem('isUserInPreviewMode')
            navigate('/')
        }
    }, [currentUser])

    const getUserStatusContent = () => {
        if (accountVerificationPending) {
            return (
                <div className={classes.bankAccountInfoDetalsBox}>
                    <Typography className={style.informationBoxHeaderText}>{t('Pages.account.AccountVerificationPending')}</Typography>
                    <div className={style.informationBoxText}>
                        {t('Pages.account.AccountVerificationPendingDescription1')}
                        {!mobile ? <br /> : ' '}
                        {isCompany
                            ? t('Pages.account.AccountVerificationPendingDescription2Company')
                            : t('Pages.account.AccountVerificationPendingDescription2Personal')}
                    </div>
                </div>
            )
        }

        if (!accountVerificationPending && isCompany) {
            return (
                <Box sx={{ textAlign: 'center' }} className={style.noTransactionContainer}>
                    <div className={style.titleWrap}>
                        <img src={successImg} className={style.successImage} alt="success" />
                        <Typography className={style.informationBoxHeaderText}>{t('OnboardingWizard.SuccessCard.Title')}</Typography>
                    </div>
                    <Typography className={style.informationBoxText}>{t('OnboardingWizard.SuccessCard.DetailsCompany')}</Typography>
                </Box>
            )
        }

        return (
            <div className={classes.warningInfoBox}>
                <Typography className={style.informationBoxHeaderText}>{t('Pages.account.AccountVerificationNotComplete')}</Typography>
                <div className={style.informationBoxText}>
                    {t('Pages.account.AccountVerificationNotCompleteDescription1')}
                    {!mobile ? <br /> : ' '}
                    <Trans i18nKey="Pages.account.AccountVerificationNotCompleteDescription2">
                        {!mobile && <span className="button" onClick={handleVerifyClick} />}
                    </Trans>
                </div>
                {mobile && (
                    <Button
                        className={clsx(classes.modalButton, style.verifyAccountButton, 'verifyAccountButton')}
                        variant="contained"
                        color="primary"
                        onClick={handleVerifyClick}
                    >
                        {t('Pages.account.VerifyAccount')}
                    </Button>
                )}
            </div>
        )
    }

    const handleVerifyClick = () => {
        navigate('/')
    }

    if (!currentUser) {
        return <></>
    }

    return (
        <HomePageLayout
            page={accountVerificationPending ? 'account-mock-pending' : 'account-mock'}
            loadingContent={!currentUser || !currencies}
        >
            <>
                <SectionHeader>{t('Pages.account.Title')}</SectionHeader>
                {getUserStatusContent()}
                <div className={style.recentTransactions}>
                    <DropDown header={t('RecentTransactions.RecentTransactionsHeader')} type="button" larger open>
                        <DropDownOptions>
                            <Button
                                type="button"
                                className={clsx(classes.buttonOutlined, style.buttonOutlined)}
                                variant="outlined"
                                color="primary"
                                disabled
                            >
                                {t('DropDownComponent.SeeAllTransactions')}
                            </Button>
                        </DropDownOptions>
                        <DropDownContent>
                            <br />
                            <div className={style.noTransactionContainer}>
                                <div className={style.transactionIconContainer}>
                                    <img className="icon" src={tooltipInfo} alt="info" />
                                </div>
                                <Typography className={style.informationBoxHeaderText}>{t('Pages.account.NoTransactions')}</Typography>
                                <div className={style.informationBoxText}>
                                    {t('Pages.account.NoTransactionsDescription1')}
                                    {!mobile ? <br /> : ' '}
                                    {t('Pages.account.NoTransactionsDescription2')}
                                </div>
                            </div>
                        </DropDownContent>
                    </DropDown>
                </div>
                <Sections dashboard={MockDashboard} mockedAssets={MockAssets} page="ledgers" />
            </>
        </HomePageLayout>
    )
}

export default withStyles(combineStyles(themeStyles.useModalStyles, themeStyles.useSettingsPageStyles))(AccountMockPage)
